import { useStateContext } from '../../contexts/ContextProvider';
import { usePermissions } from '../../contexts/PermissionContext';
import { estModuleApplicable } from '../../domain/modulesDashboard';
import { ACCES_MODULES_MENAGE, refusAccesModule, type AccesModule } from '../../domain/accesModulesDashboard';
import { useDashboards } from '../../api/dashboard/useDashboards';

import EtatErreur from '../components/EtatErreur';
import Screen from '../components/Screen';
import Text from '../components/Text';
import EnteteApp from '../shell/EnteteApp';
import { useCoque } from '../shell/CoqueContext';
import { ECRANS_MOBILES, estEcranEligible } from '../config/mobileScreens';
import { echecSansDonnee } from '../helpers/requete';
import { t } from '../i18n/langue';

import { CarteDeRepli } from './dashboard/briques';
import { ModuleIndicateurs } from './dashboard/modules';
import { ModuleEtatChambres, ModuleObjetsTrouves, ModulePannes } from './dashboard/modulesMenage';

/**
 * Le tableau de bord mobile, en lecture seule — décisions #22, #15, #27, #87.
 *
 * ## Une colonne, à hauteur naturelle
 *
 * **Aucun défilement imbriqué** : une zone défilante interne se dispute le
 * balayage de retour, seul retour d'une PWA iOS (#17). Le seul défileur est celui
 * de `Screen`.
 *
 * **`grid_h` est ignoré.** C'est l'empreinte du backfill de #15 — 122 à 124
 * valeurs identiques par module sur ~125 — pas une intention. Le reproduire
 * donnerait force de loi à un accident.
 *
 * **Aucun contrôle de période.** Il n'en existe aucun au niveau dashboard sur le
 * web, et seuls 4 `config` sont non vides sur ~870 widgets : le `config` persisté
 * est honoré sans être offert. Le mobile ne diverge pas *vers le haut*, sous peine
 * de créer une attente que le bureau ne sait pas tenir.
 *
 * ## Le scope groupe masque, il ne remplace pas
 *
 * ⚠️ La subtilité de #27, qui se perd facilement : **il n'y a rien à cacher**.
 * `reservations` est à zéro sur les 19 groupes réels depuis toujours, et les neuf
 * modules concernés en dérivent tous.
 *
 * Mais « Arrivées aujourd'hui : 0 » se lit comme une **mesure**, pas comme une
 * inapplicabilité — et la carte de repli est disqualifiée par son propre libellé,
 * « consultable sur ordinateur » étant faux quand il n'y a rien à y consulter.
 * Donc : masquage au rendu, sans rien à la place.
 */

interface ModulePorte {
    Rendu: () => JSX.Element;
    /**
     * Qui peut le voir. Le dashboard est partagé par tout l'hôtel : sans ce test,
     * un widget posé par la direction appellerait l'API (403) chez la réception.
     */
    acces?: AccesModule;
}

/** Ce que le mobile sait rendre. Le reste éligible tombe en carte de repli. */
const MODULES_PORTES: Record<string, ModulePorte> = {
    kpis: { Rendu: ModuleIndicateurs },
    housekeeping_overview: { Rendu: ModuleEtatChambres, acces: ACCES_MODULES_MENAGE.housekeeping_overview },
    maintenance_open: { Rendu: ModulePannes, acces: ACCES_MODULES_MENAGE.maintenance_open },
    lost_found_unclaimed: { Rendu: ModuleObjetsTrouves, acces: ACCES_MODULES_MENAGE.lost_found_unclaimed },
};

export default function Dashboard() {
    const { ouvrirPanneau } = useCoque();
    const { currentFacility } = useStateContext();
    const { hasPermission, isSuperAdmin } = usePermissions();
    const { data, isLoading, isError, refetch } = useDashboards();

    const estGroupe = currentFacility?.is_group === true;
    // Un rechargement de `/dashboards` en échec ne masque plus les widgets déjà
    // reçus (audit 2026-09-23, A6).
    const echec = echecSansDonnee({ isError, data });

    // Le dashboard par défaut, comme le web : le mobile n'offre pas d'en choisir
    // un autre — ce serait un réglage de plus pour un écran de consultation.
    const tableau = data?.data?.find((d) => d.is_default) ?? data?.data?.[0];

    /*
     * Les cartes de repli des écrans NON PORTÉS (#22), sous exactement le même
     * test d'éligibilité que les écrans portés — sans quoi une carte « Fidélité »
     * apprendrait à un agent d'entretien à la fois la configuration de l'hôtel et
     * l'existence de ce qu'il n'a pas le droit de voir (#17).
     */
    const replis = ECRANS_MOBILES.filter(
        (ecran) => !ecran.porte && estEcranEligible(ecran, hasPermission, isSuperAdmin === true, currentFacility ?? null),
    );

    const widgetsPortes = (tableau?.widgets ?? [])
        // L'ordre du web, sans reproduire ses hauteurs.
        .slice()
        .sort((a, b) => a.grid_y - b.grid_y || a.grid_x - b.grid_x)
        .filter((w) => estModuleApplicable(w.module_type, estGroupe))
        .filter((w) => {
            const porte = MODULES_PORTES[w.module_type];
            if (!porte) return false;
            // Hors des droits ou module coupé : masqué, comme le scope groupe. Une
            // carte de repli dirait « consultable sur ordinateur », ce qui est faux.
            return !porte.acces || refusAccesModule(porte.acces, hasPermission, currentFacility ?? null) === null;
        });

    // Ni widget rendable, ni carte de repli : l'écran ne doit pas rester
    // silencieux (audit 2026-09-23). Un établissement SANS tableau aussi : il
    // n'a rien à montrer non plus (relecture M5).
    const rienAMontrer = !isLoading && !echec && widgetsPortes.length === 0 && replis.length === 0;

    return (
        <Screen entete={<EnteteApp titre={t('dashboard.titre')} onOuvrirPanneau={ouvrirPanneau} />}>
            <div className="flex flex-col gap-3 px-4 py-4">
                {isLoading && (
                    <Text variante="petit" ton="attenue">
                        {t('commun.chargement')}
                    </Text>
                )}

                {echec && <EtatErreur onReessayer={() => void refetch()} />}

                {!isLoading && !echec && widgetsPortes.map((w) => {
                    const { Rendu } = MODULES_PORTES[w.module_type];
                    return <Rendu key={w.id} />;
                })}

                {/* Les replis ne dépendent que des permissions : ni le chargement
                    ni l'échec de `/dashboards` ne les concernent (relecture M5). */}
                {replis.map((ecran) => (
                    <CarteDeRepli key={ecran.cle} libelle={ecran.libelle} />
                ))}

                {rienAMontrer && (
                    <Text variante="petit" ton="attenue">
                        {t('dashboard.rienAMontrer')}
                    </Text>
                )}
            </div>
        </Screen>
    );
}
